import { setPricingDialogOpen } from "#webapp/src/state/actions/payments.actions";
import {
  useGatewayMutation,
  useGatewayQuery,
} from "@clockwise/web-commons/src/network/apollo/gateway-provider";
import { useReadActiveEvent } from "@clockwise/web-commons/src/util/ActiveEventContext";
import { track, TrackingEvents } from "@clockwise/web-commons/src/util/analytics.util";
import { shouldShowJoinExistingPlanWithRedux } from "@clockwise/web-commons/src/util/feature-grid.util";
import { openBillingPage } from "@clockwise/web-commons/src/util/upgradeLinks.util";
import React, { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { IReduxState } from "../../../state/reducers/root.reducer";
import { History } from "../atoms/history/History";
import { EventHistoryDocument } from "./__generated__/EventHistory.v2.generated";
import { UnpauseEventDocument } from "./__generated__/UnpauseEvent.v2.generated";

export const ECHistory = () => {
  const activeEvent = useReadActiveEvent();
  const eventId = activeEvent?.externalEventId ?? null;
  const calendarId = activeEvent?.calendarId ?? null;

  const dispatch = useDispatch();
  const featureGrid = useSelector((state: IReduxState) => state.featureGrid);
  const orgBillingGroupIds = useSelector((state: IReduxState) => state.payments.orgBillingGroupIds);
  const shouldShowJoinExistingPlan = shouldShowJoinExistingPlanWithRedux(
    orgBillingGroupIds ?? [],
    featureGrid.hasPaidPlan,
  );

  const handleViewPlans = () => {
    dispatch(setPricingDialogOpen(true, undefined));
  };

  const handleUnpauseEvent = () => {
    if (!eventId) {
      return;
    }

    void unpauseEvent({
      variables: {
        externalEventId: eventId,
      },
    });

    track(TrackingEvents.DEFRAG.UNPAUSE_AUTOPILOT, {
      externalEventId: eventId,
    });
  };

  const handleGoToBilling = () => {
    openBillingPage();
  };

  const trackTimelineExpanded = useCallback(() => {
    track(TrackingEvents.AUTOPILOT_HISTORY.SIDEPANEL_FLEXIBLE_MEETING_HISTORY_SHOW_MORE, {
      externalEventId: eventId,
    });
  }, [eventId]);

  const [unpauseEvent] = useGatewayMutation(UnpauseEventDocument, {
    refetchQueries: [{ query: EventHistoryDocument }],
  });

  const { data, loading, error } = useGatewayQuery(EventHistoryDocument, {
    variables: {
      id: eventId!,
      calendarId: calendarId!,
    },
    skip: !eventId || !calendarId,
  });

  const nextDefrag = data?.currentOrg?.nextDefragTime ?? null;
  const entries = data?.event?.history.entries ?? [];
  const status = data?.event?.history.status ?? null;
  const flexibility = data?.event?.flexibility ?? null;

  return (
    <History
      entries={entries}
      error={error}
      flexibility={flexibility}
      loading={loading}
      nextDefrag={nextDefrag}
      onGoToBilling={handleGoToBilling}
      onTimelineExpanded={trackTimelineExpanded}
      onUnpauseEvent={handleUnpauseEvent}
      onViewPlans={handleViewPlans}
      showJoinExistingPlan={shouldShowJoinExistingPlan}
      status={status}
    />
  );
};
